import { Injectable } from '@angular/core';
import {
  ITurnstile,
  ITurnstileWindow,
} from '@merchant-portal/app/models/turnstile/turnstile.interface';
import { environment } from '@merchant-portal/environments/environment';
import { catchError, from, map, Observable, of, timeout } from 'rxjs';
import {
  TURNSTILE_ACTION,
  TURNSTILE_MAX_ATTEMPTS,
  TURNSTILE_SCRIPT_URL,
  TURNSTILE_TIMEOUT_MS,
} from './constant/turnstile.constant';

@Injectable({
  providedIn: 'root',
})
export class TurnstileService {
  private scriptLoader?: Promise<ITurnstile>;

  /**
   * Returns the Cloudflare Turnstile headers for the given action.
   * An empty object is returned when the token can not be generated, so the request is still sent
   * and backend-portal decides whether the request is rejected.
   */
  getHeaders(action: TURNSTILE_ACTION): Observable<Record<string, string>> {
    if (!environment.turnstile.siteKey) {
      return of({});
    }

    return from(this.getToken(action)).pipe(
      timeout(TURNSTILE_TIMEOUT_MS),
      map(token => ({
        'X-Turnstile-Token': token,
        'X-Turnstile-Widget': environment.turnstile.widget,
      })),
      catchError(error => {
        console.error(error);
        return of({});
      })
    );
  }

  /**
   * Starts downloading the Turnstile script in the background so the time
   * budget of a later getHeaders() call is spent on the challenge itself,
   * not on the script download. Safe to call multiple times; download
   * failures are ignored here and surface on the next getHeaders() call.
   */
  preload(): void {
    if (!environment.turnstile.siteKey) {
      return;
    }
    this.loadScript().catch(() => undefined);
  }

  private async getToken(action: TURNSTILE_ACTION): Promise<string> {
    const turnstile = await this.loadScript();

    return new Promise((resolve, reject) => {
      // The invisible widget does not render any UI, the container is only needed to run the challenge
      const container = document.createElement('div');
      document.body.appendChild(container);

      let widgetId: string | undefined;
      let settled = false;
      let attempt = 0;
      let deadline: ReturnType<typeof setTimeout>;

      const cleanup = () => {
        clearTimeout(deadline);
        if (widgetId !== undefined) {
          turnstile.remove(widgetId);
          widgetId = undefined;
        }
        container.remove();
      };
      // Removing the widget synchronously inside a callback races with the script's
      // internal auto-retry, which is scheduled after the callback returns and then
      // throws "Widget ... to reset was not found". Deferring the cleanup by one
      // macrotask lets the script finish its handling first, and turnstile.remove()
      // also cancels the pending retry timer.
      const scheduleCleanup = () => setTimeout(cleanup);

      const settle = (finish: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        finish();
      };
      const giveUp = (error: Error) =>
        settle(() => {
          scheduleCleanup();
          reject(error);
        });

      // Failsafe so an unresponsive challenge cannot outlive the retry budget.
      // Also cleans up the widget when the rxjs timeout in getHeaders() has
      // already abandoned this promise.
      deadline = setTimeout(
        () => giveUp(new Error('Turnstile challenge exceeded the time budget')),
        TURNSTILE_TIMEOUT_MS
      );

      widgetId = turnstile.render(container, {
        sitekey: environment.turnstile.siteKey,
        action,
        // Cloudflare spaces failure-retries 2s + retry-interval (default 8s),
        // which allows only one retry within the time budget. 1 is the minimum
        // valid value (must be an integer > 0), keeping the spacing at ~2s.
        'retry-interval': 1,
        callback: token =>
          settle(() => {
            scheduleCleanup();
            resolve(token);
          }),
        // Not terminal: with the default retry mode the script resets the widget
        // itself and delivers a fresh token to the callback on a later attempt.
        // Give up only once the attempts or the time budget are exhausted.
        'error-callback': errorCode => {
          attempt++;
          console.warn(`Turnstile challenge attempt ${attempt} failed: ${errorCode}`);
          if (attempt >= TURNSTILE_MAX_ATTEMPTS) {
            giveUp(new Error(`Turnstile challenge failed after ${attempt} attempts: ${errorCode}`));
          }
          return true;
        },
        'timeout-callback': () => giveUp(new Error('Turnstile challenge timed out')),
      });
    });
  }

  private loadScript(): Promise<ITurnstile> {
    const turnstileWindow = window as ITurnstileWindow;
    if (turnstileWindow.turnstile) {
      return Promise.resolve(turnstileWindow.turnstile);
    }

    this.scriptLoader ??= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () =>
        turnstileWindow.turnstile
          ? resolve(turnstileWindow.turnstile)
          : reject(new Error('Turnstile is not available'));
      script.onerror = () => {
        this.scriptLoader = undefined;
        script.remove();
        reject(new Error('Failed to load Turnstile script'));
      };
      document.head.appendChild(script);
    });

    return this.scriptLoader;
  }
}
